import { CARDS, ERA_1_POOL, pairingStages, type EraPoolDef } from "@reagent/content";
import type { CardInstance } from "@reagent/sim";
import type { RunState, RunStatus, UpgradeKind } from "./types";

/**
 * Saving and loading a run.
 *
 * Storage itself is the caller's problem — this package stays free of browser
 * globals so it can be tested in node. What lives here is the part that is
 * easy to get wrong: deciding whether a blob found on disk is actually a run
 * this build can use.
 *
 * Anything unrecognised is discarded rather than repaired. A half-understood
 * save that loads into a broken run is worse than starting again, and the
 * schema will move for as long as the game is being built.
 */
export const SAVE_VERSION = 7;

interface SaveEnvelope {
  readonly version: number;
  readonly run: RunState;
}

export function serializeRun(run: RunState): string {
  const envelope: SaveEnvelope = { version: SAVE_VERSION, run };
  return JSON.stringify(envelope);
}

const STATUSES: readonly RunStatus[] = ["picking", "inProgress", "shopping", "won", "lost"];

/** Returns the run, or null if this isn't one we can safely resume. */
export function deserializeRun(raw: string | null, pool: EraPoolDef = ERA_1_POOL): RunState | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) return null;
  const envelope = parsed as Partial<SaveEnvelope>;
  if (envelope.version !== SAVE_VERSION) return null;

  const run = envelope.run as Partial<RunState> | undefined;
  if (!run) return null;

  const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((v) => typeof v === "string");

  // A save can predate a card rename (e.g. spec 0007's hazard id renames) and
  // still match SAVE_VERSION today — the `cardId in CARDS` check catches it,
  // so a stale deck fails to load cleanly instead of failing mid-play as
  // "unknown card".
  const isCardInstanceArray = (value: unknown): value is CardInstance[] =>
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === "object" &&
        v !== null &&
        typeof (v as Partial<CardInstance>).instanceId === "string" &&
        typeof (v as Partial<CardInstance>).cardId === "string" &&
        typeof (v as Partial<CardInstance>).acquiredStageIndex === "number" &&
        ((v as Partial<CardInstance>).boundNodeId === null ||
          typeof (v as Partial<CardInstance>).boundNodeId === "string") &&
        typeof (v as Partial<CardInstance>).isContaminated === "boolean" &&
        (v as CardInstance).cardId in CARDS,
    );

  if (typeof run.seed !== "number" || !Number.isFinite(run.seed)) return null;
  if (typeof run.stageIndex !== "number" || run.stageIndex < 0) return null;
  if (typeof run.credits !== "number" || run.credits < 0) return null;
  if (typeof run.nextInstanceSeq !== "number") return null;
  if (!STATUSES.includes(run.status as RunStatus)) return null;
  if (!isCardInstanceArray(run.deck)) return null;
  // Only a run that has not picked yet may hold no cards; every other status
  // has a deck.
  const picking = run.status === "picking";
  if (picking ? run.deck.length !== 0 : run.deck.length === 0) return null;

  // Ids are checked against the pool the caller resolves saves with: a pathway
  // renamed or removed since the save was written fails to load cleanly.
  const pathways = run.pathways as unknown;
  let validPathways: readonly [string, string] | null;
  if (pathways === null) {
    validPathways = null;
  } else if (
    Array.isArray(pathways) &&
    pathways.length === 2 &&
    pathways[0] !== pathways[1] &&
    pathways.every((id) => typeof id === "string" && pool.pathways.some((p) => p.id === id))
  ) {
    validPathways = [pathways[0] as string, pathways[1] as string];
  } else {
    return null;
  }
  if (picking && validPathways !== null) return null;
  // The legacy branch is gone (spec 0037): every run past the pick has a pair.
  if (!picking && validPathways === null) return null;
  if (!isStringArray(run.upgrades)) return null;
  if (!Array.isArray(run.results) || !Array.isArray(run.choices)) return null;
  // A result naming a stage this run does not play (e.g. one of the deleted
  // legacy stages) comes from a build whose stages have since changed.
  if (validPathways !== null) {
    const stageIds = new Set(pairingStages(pool, validPathways[0], validPathways[1]).map((s) => s.id));
    const playsStage = (r: unknown): boolean =>
      typeof r === "object" && r !== null && stageIds.has((r as { stageId?: string }).stageId as string);
    if (!run.results.every(playsStage)) return null;
  }

  return {
    seed: run.seed >>> 0,
    stageIndex: run.stageIndex,
    status: run.status as RunStatus,
    pathways: validPathways,
    deck: run.deck,
    nextInstanceSeq: run.nextInstanceSeq,
    upgrades: run.upgrades as UpgradeKind[],
    credits: run.credits,
    results: run.results,
    choices: run.choices,
  };
}
